<template>
  <el-form label-position="top">
    <el-form-item label="Тип заказа" :class="{ 'is-error': showErrors && !orderType }">
      <el-radio-group v-model="orderType">
        <el-radio-button
          v-for="(label, value) in orderTypes"
          :key="value"
          :value="value"
        >
          {{ label }}
        </el-radio-button>
      </el-radio-group>
      <div v-if="showErrors && !orderType" class="el-form-item__error">Выберите тип заказа</div>
    </el-form-item>
    <el-form-item label="Валюта" :class="{ 'is-error': showErrors && !currency }">
      <div class="currency-radio-group" v-loading="loading">
        <el-radio-group v-model="currency">
          <el-radio 
            v-for="rate in exchangeRates" 
            :key="rate.currency" 
            :label="rate.currency"
            :disabled="rate.currency === CurrencyEnum.USDT"
            class="currency-radio-item"
          >
            <div class="currency-option">
              <img :src="getCurrencyFlagUrl(rate.currency)" class="currency-flag" />
              <span class="currency-code">{{ rate.currency }}</span>
              <span class="rate-value">1 {{ rate.currency }} = {{ rate.rate }} RUB</span>
            </div>
          </el-radio>
        </el-radio-group>
      </div>
      <div v-if="showErrors && !currency" class="el-form-item__error">Выберите валюту</div>
    </el-form-item>
    <el-form-item label="Сумма заказа" :class="{ 'is-error': showErrors && (!amount || amount <= 0) }">
      <el-input-number 
        v-model="amount"
        :min="0" 
        :precision="2"
        :controls="false"
        placeholder="Сумма заказа в валюте"
        class="amount-input"
      />
      <div v-if="showErrors && (!amount || amount <= 0)" class="el-form-item__error">Введите сумму заказа</div>
    </el-form-item>
  </el-form>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import type { Order, ExchangeRate } from '@/store/types'
import { OrderTypeEnum, CurrencyEnum, ContactMethodEnum } from '@/store/types'
import { getCurrencyFlagUrl } from '@/utils/currency'

export default defineComponent({
  name: 'OrderDetails',
  setup() {
    const store = useStore()
    const loading = ref(false)
    const showErrors = ref(false)

    const orderTypes = {
      [OrderTypeEnum.PAY_INVOICE_FOR_GOODS]: 'Оплата по инвойсу за товары',
      [OrderTypeEnum.CARGO_WITH_DOCUMENTS]: 'Карго с документами',
    }

    // Computed properties with getters and setters for each form field
    const orderType = computed({
      get: () => store.state.currentOrder.order_type,
      set: (value) => {
        store.commit('UPDATE_ORDER_FIELD', { field: 'order_type', value })
      }
    })

    const currency = computed({
      get: () => store.state.currentOrder.currency,
      set: (value) => {
        store.commit('UPDATE_ORDER_FIELD', { field: 'currency', value })
      }
    })

    const amount = computed({
      get: () => store.state.currentOrder.amount,
      set: (value) => {
        store.commit('UPDATE_ORDER_FIELD', { field: 'amount', value })
      }
    })

    // Watch for validation
    watch(() => store.getters.isOrderDetailsValid, (isValid) => {
      if (!isValid) {
        showErrors.value = true
      }
    })

    const exchangeRates = computed<ExchangeRate[]>(() => store.state.exchangeRates)

    const fetchExchangeRates = async () => {
      loading.value = true
      try {
        await store.dispatch('fetchExchangeRates')
      } catch (error) {
        console.error('Failed to fetch exchange rates:', error)
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      fetchExchangeRates()
    })

    return {
      orderType,
      currency,
      amount,
      loading,
      showErrors,
      exchangeRates,
      getCurrencyFlagUrl,
      CurrencyEnum,
      ContactMethodEnum,
      orderTypes,
      OrderTypeEnum
    }
  }
})
</script>

<style scoped>
.currency-option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.currency-flag {
  width: 30px;
  height: 20px;
  object-fit: contain;
  flex-shrink: 0;
}

.currency-code {
  font-weight: bold;
  min-width: 40px;
  flex-shrink: 0;
}

.rate-value {
  margin-left: auto;
  color: #909399;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.currency-radio-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.currency-radio-item {
  margin-right: 0 !important;
  height: auto;
  padding: 12px;
  width: 100%;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  transition: all 0.3s;
  box-sizing: border-box;
}

.currency-radio-item:hover {
  border-color: #409eff;
  background-color: #f5f7fa;
}

.currency-radio-item.is-checked {
  border-color: #409eff;
  background-color: #ecf5ff;
}

.amount-input {
  width: 100%;
  max-width: 300px;
}

@media (max-width: 480px) {
  :deep(.el-radio-button__inner) {
    padding: 8px 12px;
    font-size: 13px;
  }
  
  .currency-radio-item {
    padding: 8px;
  }
  
  .rate-value {
    font-size: 12px;
  }
}
</style> 